import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  type ValidationErrors,
  type ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import * as AccountExistence from '../../../server/routes/auth/account-existence';
import * as AuthEmailConfirmPost from '../../../server/routes/auth/confirm-email/post';
import {
  confirmEmailCodeRegexString,
  generatePath,
} from '../../../server/services/utils.service';
import { AnalyticsService } from '../analytics.service';
import { AuthUserService } from '../auth-user.service';
import { HomepageLinksService } from '../homepage-links.service';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-confirm-email',
  templateUrl: './confirm-email.component.html',
  styleUrls: ['./confirm-email.component.scss'],
  standalone: false,
})
export class ConfirmEmailComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private analytics = inject(AnalyticsService);
  private titleService = inject(TitleService);
  private utils = inject(UtilsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  public homepageLinks = inject(HomepageLinksService);

  state:
    | 'emailSent'
    | 'accountExistsLogIn'
    | 'emailConfirmedLogIn'
    | 'linkExpiredRegister'
    | 'checkingCode'
    | 'linkError'
    | 'unknownError'
    | 'loading' = 'loading';

  waiting = false;

  email = '';
  accountId: string | undefined;
  expiresAt: number;
  confirmEmailCodeLink?: string; // only visible in debug

  codeForm = new FormGroup({
    code: new FormControl('', {
      validators: [Validators.required, codeValidator()],
      nonNullable: true,
    }),
  });
  codeFormErrorMessage?: string;
  codeFormDisableSubmit = true;

  loggedInRedirectUrl = '/';

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Confirm email');

    const sentMode = this.route.snapshot.queryParams?.sentMode;
    const email = this.route.snapshot.queryParams?.email;
    if (email) this.email = email;
    const accountId: string | undefined =
      this.route.snapshot.queryParams?.accountId;
    const confirmEmailCode = this.route.snapshot.queryParams?.confirmEmailCode;
    this.expiresAt = Number(this.route.snapshot.queryParams?.expiresAt);
    this.confirmEmailCodeLink =
      this.route.snapshot.queryParams?.confirmEmailCodeLink;

    if (this.expiresAt < Date.now()) {
      this.state = 'linkExpiredRegister';
      // Test account existence
      if (accountId) {
        this.http
          .get<AccountExistence.ResBody>(
            generatePath<AccountExistence.ReqParams>(
              '/api/auth/account-existence/:accountId',
              { accountId }
            )
          )
          .subscribe(({ existing }) => {
            this.state = existing
              ? 'accountExistsLogIn'
              : 'linkExpiredRegister';
          });
      } else {
        console.error('expiresAt provided but accountId not provided');
      }
    } else if (confirmEmailCode && email && accountId) {
      this.postConfirmEmailCode({ email, accountId, confirmEmailCode });
    } else if (email && sentMode) {
      this.state = 'emailSent';

      // Code form
      const codeFormSubscribe = this.codeForm.valueChanges.subscribe(() => {
        const code = this.codeForm.controls.code.value;
        const trimmed = code.trim();
        if (code !== trimmed)
          return this.codeForm.controls.code.setValue(trimmed); // Go through this again with the trimmed value

        if (this.codeForm.controls.code.errors) {
          this.codeFormDisableSubmit = true;
        } else {
          this.codeFormDisableSubmit = false;
        }
      });
      this.subscriptions.add(codeFormSubscribe);
    } else {
      this.state = 'linkError';
    }

    // If the user is already logged in (say user clicks back button or
    // reloads) redirect immediately
    const authUserSubscribe = this.authUserService.account
      .pipe(take(1))
      .subscribe((user) => {
        if (user && user.email === email)
          this.router.navigateByUrl(this.loggedInRedirectUrl);
      });
    this.subscriptions.add(authUserSubscribe);
  }

  formPostConfirmEmailCode() {
    const confirmEmailCode = this.codeForm.controls.code.value;
    const { email, accountId } = this;
    this.postConfirmEmailCode({ email, accountId, confirmEmailCode });
  }

  postConfirmEmailCode({
    email,
    accountId,
    confirmEmailCode,
  }: {
    email: string;
    accountId?: string;
    confirmEmailCode: string;
  }) {
    const body: AuthEmailConfirmPost.ReqBody = {
      email,
      accountId,
      confirmEmailCode,
    };

    this.state = 'checkingCode';

    this.waiting = true;
    const httpSubscribe = this.http
      .post<AuthEmailConfirmPost.ResBody>('/api/auth/confirm-email', body)
      .subscribe({
        next: (authResult) => {
          this.waiting = false;
          if (authResult) {
            this.authUserService.processRegister(authResult);
            this.analytics.register('email');
            this.router.navigate(['/']);
          } else {
            this.state = 'emailConfirmedLogIn';
          }
        },
        error: (error) => {
          this.waiting = false;
          if (error.status === 0) {
            this.utils.launchOfflineNotification();
          } else if (error.status === 409) {
            this.state = 'accountExistsLogIn';
          } else if (error.status === 400) {
            this.state = 'linkError';
          } else {
            this.state = 'unknownError';
            console.error(error);
          }

          this.codeFormErrorMessage = undefined;
        },
      });
    this.subscriptions.add(httpSubscribe);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

function codeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const idRegEx = new RegExp(confirmEmailCodeRegexString);
    if (idRegEx.test(control.value)) return null;
    return { token: 'token failed validation' };
  };
}
